<template>
  <div class="tw-flex tw-justify-center" :style="`--primary-500: ${primaryValue}`">
    <base-card shadow class="tw-overflow-hidden tw-w-full sm:tw-max-w-md">
      <div class="tw-flex tw-my-3 tw-justify-center">
        <avatar class="tw-my-auto tw-mr-1" :height="100" :width="100" :businessId="businessId" :avatarType="'business'" :editable="false" :rounded="false" />
      </div>
      <div class="tw-text-center tw-text-gray-800 tw-text-lg tw-font-light tw-mb-3">Create an Account</div>
      <div v-if="operatingModel === 'direct'" class="tw-text-left tw-text-gray-800 tw-font-light tw-text-sm">
        {{ businessName }} uses the Predictile technology platform to enable easy and secure savings plans. Create an account to activate your savings plan.
      </div>
      <div v-else class="tw-text-left tw-text-gray-800 tw-font-light tw-text-sm">
        {{ location.name }} uses the Predictile technology platform to enable easy and secure savings plans. Create an account to activate your savings plan.
      </div>
      <div class="tw-my-3">
        <div class="tw-text-right tw-text-gray-600 tw-text-xs">Already have an account?</div>
        <div class="tw-flex tw-w-full tw-justify-end">
          <base-button type="text" size="sm" :style="`--primary-500: ${secondaryValue}; --primary-600: ${secondaryValue};`" @click="signIn">Sign In</base-button>
        </div>
      </div>
      <base-form ref="form" v-model="isFormValid">
        <div class="tw-text-gray-800">Account Information</div>
        <base-text-field
          label="Full Name (First and Last)"
          placeholder="Please enter your full name"
          v-model="name"
          class="tw-mt-6"
          required
          :maxlength="255"
          :readonly="isLoading"
        />
        <base-email-field label="Email Address" required v-model="email" :readonly="isLoading" />
        <base-text-field label="Password" type="password" v-model="password" required class="tw-mt-2" />
        <base-text-field
          label="Confirm password"
          type="password"
          placeholder="Enter you new password again"
          v-model="confirmPassword"
          :rules="passwordRules"
          required
          :minlength="6"
          class="tw-mt-2"
        />
        <base-phone-number-input label="Mobile Phone" v-model="phone" :e164.sync="phone164" required />
        <div v-if="collectDobSetting === true">
          <base-date-picker label="Date of Birth" initial-view="year" v-model="dob" required />
        </div>
        <div v-if="collectAddressSetting === false">
          <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="postalCode" required />
        </div>

        <div v-if="collectAddressSetting === true" class="tw-mt-4">
          <div class="tw-text-gray-800">Mailing Address</div>
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
            <base-text-field class="tw-col-span-2" label="Line 1" placeholder="Enter line 1" v-model="address.line1" required />
            <base-text-field label="Line 2" placeholder="Enter line 2" v-model="address.line2" />
          </div>
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-x-4">
            <base-text-field label="City" placeholder="Enter city" v-model="address.city" required />
            <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="address.state" required />
            <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="address.postalCode" required />
          </div>
        </div>

        <div class="tw-text-gray-800 tw-mt-4">Billing Details</div>
        <base-credit-card-input class="tw-mt-3" ref="creditCardInput" />

        <div>
          <span class="tw-text-gray-800">Plan Details:</span>
          <base-list-group class="tw-mt-2">
            <base-list-item v-for="(plan, index) in selectedPlans" :key="plan.businessPlanId" :showBottomBorder="index != selectedPlans.length - 1">
              <div class="tw-flex tw-justify-between">
                <div class="tw-text-gray-800 tw-text-sm">{{ plan.name }}</div>
                <base-button :style="`--primary-500: ${secondaryValue}; --primary-600: ${secondaryValue};`" type="text" @click="planTermsModal(plan.businessPlanId)">
                  Terms
                </base-button>
              </div>
            </base-list-item>
          </base-list-group>
        </div>
        <div class="tw-my-4">
          <base-checkbox-group required v-model="termsAcceptance">
            <base-checkbox
              label="I agree to the plan fees plus applicable taxes and service fees for the plans detailed above for the duration of the plan terms."
              value="isAccepted"
              v-model="termsAcceptance"
              required
            />
          </base-checkbox-group>
        </div>
      </base-form>
      <inline-error id="signup-card-error" class="tw-my-3" :title="errorTitle" :message="errorMessage" :error="error" />
      <div class="tw-flex tw-w-full">
        <base-button
          class="tw-w-full"
          :style="`--primary-500: ${secondaryValue}; --primary-600: ${secondaryValue};`"
          type="primary"
          @click="submit()"
          :loading="isLoading"
          :disabled="isLoading"
        >
          <span> Let's get started! </span>
        </base-button>
      </div>
    </base-card>
    <base-modal ref="planTermsModal">
      <base-card>
        <div class="tw-flex tw-justify-between tw-mb-6">
          <div class="tw-text-xl tw-font-thin tw-my-auto">{{ termsBusinessPlan.name }} Plan Terms and Conditions</div>
          <base-icon class="tw-my-auto"><x-icon class="tw-w-5 tw-h-5" @click="planTermsModalHide"/></base-icon>
        </div>
        <business-plan-agreement
          :businessPlanId="termsBusinessPlan.businessPlanId"
          :priceCents="termsBusinessPlan.priceCents"
          :currency="termsBusinessPlan.priceCurrency"
          :downPaymentCents="termsBusinessPlan.downPaymentCents"
          :businessName="businessName"
          :termLength="termsBusinessPlan.termLength"
          :termInterval="termsBusinessPlan.termInterval"
          :cycleFrequency="termsBusinessPlan.cycleFrequency"
          :cycleInterval="termsBusinessPlan.cycleInterval"
          :serviceFeeCents="termsBusinessPlan.serviceFeeCents"
        />
        <div class="tw-flex tw-w-full tw-justify-end">
          <base-button @click="planTermsModalHide" type="secondary" color="gray" class=" sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Close
          </base-button>
        </div>
      </base-card>
    </base-modal>
  </div>
</template>

<script>
import InlineError from "@/components/InlineError";
import Avatar from "@/components/Avatar.vue";
import BusinessPlanAgreement from "@/views/business/business-plans/components/BusinessPlanAgreement.vue";
import moment from "moment";
import { STATE_OPTIONS } from "@/constants/constants";
import { XIcon } from "@/assets/svgs";

export default {
  props: {
    businessId: String,
    businessName: String,
    primaryColor: String,
    secondaryColor: String,
    operatingModel: String,
    location: Object,
    selectedPlans: Array,
    collectAddressSetting: Boolean,
    collectDobSetting: Boolean,
  },
  components: {
    InlineError,
    Avatar,
    BusinessPlanAgreement,
    XIcon,
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      passwordRules: [
        () => this.password === this.confirmPassword || "Please enter matching passwords",
        () => this.password.length > 7 || "Minimum Password lenth is 8 characters",
      ],
      termsBusinessPlan: {},
      termsAcceptance: [],
      name: null,
      email: null,
      postalCode: null,
      password: null,
      confirmPassword: null,
      token: null,
      phone: "",
      phone164: "",
      isPhoneValid: false,
      showPhoneInvalidValidation: false,
      componentKey: 0,
      address: {
        line1: null,
        line2: null,
        city: null,
        state: null,
        zip: null,
      },
      dob: null,
      errorTitle: "",
      errorMessage: "",
      error: null,
      STATE_OPTIONS,
    };
  },
  computed: {
    locale() {
      return window.navigator.language;
    },
    primaryValue() {
      return this.primaryColor ? this.primaryColor : "#ff0090";
    },
    secondaryValue() {
      return this.secondaryColor ? this.secondaryColor : "#404040";
    },
  },
  created() {
    this.email = this.$route.query.e;
  },
  mounted() {
    this.termsBusinessPlan = this.selectedPlans[0];
  },
  methods: {
    async submit() {
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;

      this.token = await this.$refs.creditCardInput.createToken();

      if (!this.$refs.form.validate() || !this.token) {
        return;
      }
      this.isLoading = true;
      this.$axios
        .post(`/businesses/${this.businessId}/locations/${this.location.businessLocationId}/member-web-signup/signup`, {
          businessId: this.businessId,
          businessLocationId: this.location.businessLocationId,
          businessPlanIds: this.selectedPlans.map((x) => x["businessPlanId"]),
          fullName: this.name,
          email: this.email,
          phoneNumber: this.phone164,
          password: this.password,
          postalCode: this.postalCode,
          tokenId: this.token.id,
          startDate: new Date(),
          address: this.address,
          dob: this.dob,
        })
        .then(() => {
          this.href(`/businesses/${this.businessId}/member-web-signup/success`);
        })
        .catch((err) => {
          this.errorTitle = `Failed to start plan`;
          this.errorMessage = `We were not able to start the plan. Please check the error message below and try again.`;
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    href(path) {
      this.$router.push(path);
    },
    signIn() {
      this.$emit("signIn");
    },
    planTermsModal(businessPlanId) {
      this.termsBusinessPlan = this.selectedPlans[this.selectedPlans.findIndex((x) => x.businessPlanId === businessPlanId)];
      this.$refs.planTermsModal.show();
    },
    planTermsModalHide() {
      this.$refs.planTermsModal.hide();
    },
    customFormatter(date) {
      return moment(date).format("MM/DD/YYYY");
    },
  },
};
</script>
